import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Shield, UserPlus, AlertCircle } from "lucide-react";
import { LangToggle } from "@/components/LangToggle";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", mobile: "",
    role: "citizen", panchayat_id: "",
  });
  const [panchayats, setPanchayats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/panchayats").then((r) => setPanchayats(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      if (!payload.panchayat_id) delete payload.panchayat_id;
      await register(payload);
      toast.success("Account created!");
      nav("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFBF7]">
      <div className="w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A3161]">
            <Shield className="w-4 h-4" /> {t("appShort")}
          </Link>
          <LangToggle />
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-8">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("register")}</div>
          <h2 className="mt-2 font-heading text-2xl font-bold text-slate-900">{t("welcome")} — {t("appShort")}</h2>

          {err && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="register-form">
            {[
              { k: "full_name", lbl: t("fullName"), type: "text", required: true },
              { k: "email", lbl: t("email"), type: "email", required: true },
              { k: "mobile", lbl: t("mobile"), type: "tel" },
              { k: "password", lbl: t("password"), type: "password", required: true },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{f.lbl}</label>
                <input
                  data-testid={`reg-${f.k}`}
                  type={f.type}
                  required={f.required}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0A3161] focus:ring-2 focus:ring-[#0A3161]/10 outline-none text-sm"
                />
              </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t("role")}</label>
                <select
                  data-testid="reg-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0A3161] outline-none text-sm bg-white"
                >
                  <option value="citizen">{t("citizen")}</option>
                  <option value="operator">Operator</option>
                  <option value="secretary">{t("secretary")}</option>
                  <option value="sarpanch">{t("sarpanch")}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t("panchayat")}</label>
                <select
                  data-testid="reg-panchayat"
                  value={form.panchayat_id}
                  onChange={(e) => setForm({ ...form, panchayat_id: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0A3161] outline-none text-sm bg-white"
                >
                  <option value="">— {t("pickPanchayat")} —</option>
                  {panchayats.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.district})</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-sm text-white"
              style={{ background: "#0A3161" }}
            >
              <UserPlus className="w-4 h-4" /> {busy ? "…" : t("register")}
            </button>
          </form>

          <div className="mt-5 text-sm text-slate-600">
            {t("haveAccount")} <Link to="/login" className="text-[#0A3161] font-semibold hover:underline">{t("login")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
